import type { FeatureDescriptor, ViewPolicy } from "./feature-contract";

function policy(
  route: string,
  overrides: Partial<ViewPolicy> = {},
): ViewPolicy {
  return {
    allowClose: true,
    allowDetach: true,
    allowDock: true,
    allowMove: true,
    allowMultiple: false,
    allowResize: true,
    defaultSize: { height: 540, width: 760 },
    featureFlag: null,
    icon: "module",
    loadingPriority: "on-demand",
    minSize: { height: 280, width: 360 },
    offline: false,
    prefetch: "never",
    route,
    supportedDevices: ["desktop", "tablet", "mobile"],
    supportedScreens: ["main", "situation", "map", "mobile-field", "tablet-command"],
    version: 1,
    ...overrides,
  };
}

export const shellFeatureDescriptors = [
  {
    availability: "ready",
    description: {
      de: "Einsatzführungstafel und Einsatztagebuch in einem konfigurierbaren Arbeitsbereich",
      en: "Incident execution and operational logbook in a configurable workspace",
    },
    id: "operations",
    /** Hosts the incident selector itself, so it must open with none selected. */
    incidentContext: "optional",
    load: () => import("../routes/operations-route"),
    moduleCode: "M08",
    policy: policy("/operations", {
      allowDetach: false,
      loadingPriority: "normal",
      supportedScreens: ["main", "situation", "tablet-command"],
    }),
    requiredPermissions: ["mission_read"],
    surfaces: ["route"],
    title: { de: "Einsatz", en: "Operations" },
  },
  {
    availability: "ready",
    description: {
      de: "Mobiler Begleiter für Alarm, Auftrag, Feldaufgabe, Lagebild, Nachweise und Rückkehr",
      en: "Mobile companion for alert, order, field task, map, evidence, and return",
    },
    id: "field-operations",
    /** Every panel binds `runtime.selectedIncidentId`. */
    incidentContext: "required",
    load: () => import("../../features/field-operations/field-operations-route"),
    moduleCode: "M09",
    policy: policy("/modules/field-operations", {
      allowDetach: false,
      loadingPriority: "normal",
      offline: true,
      supportedDevices: ["desktop", "tablet", "mobile"],
      supportedScreens: ["main", "tablet-command", "mobile-field"],
    }),
    permissionMode: "any",
    requiredPermissions: [
      "mission_read",
      "order_read",
      "order_acknowledge",
      "registration_overview_read",
      "registration_movement_record",
      "form_read",
      "form_submit",
      "map_object_read",
      "offline_map_use",
    ],
    surfaces: ["route"],
    title: { de: "Feldeinsatz", en: "Field Operations" },
  },
  {
    availability: "ready",
    description: {
      de: "Gemeinsames Lagebild als eigener Vollbild-Reiter (2D/3D, veröffentlichte Objekte)",
      en: "Common operating picture as its own full-size tab (2D/3D, published objects)",
    },
    id: "lagekarte",
    /** Wraps the incident-scoped situation map. */
    incidentContext: "required",
    load: () => import("../../features/situation-map/lagekarte-route"),
    moduleCode: "M14",
    // Incident-bound: `/?open=lagekarte` mounts the REAL situation-map panel
    // full-size under the operations runtime, with the Active-incidents rail in
    // the left border. The standalone route had neither.
    policy: policy("/?open=lagekarte", {
      loadingPriority: "normal",
      supportedScreens: ["main", "situation", "map", "tablet-command"],
    }),
    requiredPermissions: ["mission_read"],
    surfaces: ["route"],
    title: { de: "Lagekarte", en: "Situation map" },
  },
  {
    availability: "ready",
    description: {
      de: "Gelenkter S2-Arbeitsbereich für Meldungseingang, unabhängige Kartenprüfung, Briefing, Veröffentlichung und regionale Projektion",
      en: "Governed S2 workspace for report intake, independent map review, briefing, publication, and regional projection",
    },
    id: "situation-cop",
    /** Reads `operationalContext.incident` for every screen. */
    incidentContext: "required",
    load: () => import("../../features/situation-cop/situation-cop-route"),
    // Spans M13/M14/M18; M13 (Lage) is the owning module — the contract's
    // `M${number}` moduleCode is a single sort/filter key, not a display list.
    moduleCode: "M13",
    // Incident-bound: hosted by the shared module workspace (Active-incidents
    // rail in the left border), never with a selector of its own.
    policy: policy("/?open=situation-cop", {
      allowDetach: false,
      loadingPriority: "normal",
      supportedScreens: ["main", "situation", "map", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: [
      "mission_read",
      "situation_report_read",
      "situation_report_create",
      "map_object_read",
      "map_object_create",
      "map_object_review",
      "map_object_publish",
      "form_read",
    ],
    surfaces: ["route"],
    title: { de: "Lage & Lagebild", en: "Situation & COP" },
  },
  {
    availability: "ready",
    description: {
      de: "Bereichsbezogene Konfigurationsaufgaben prüfen",
      en: "Review scope-bound configuration tasks",
    },
    id: "administration",
    /** Organization and configuration scope. */
    incidentContext: "none",
    load: () => import("../routes/administration-route"),
    moduleCode: "M19",
    policy: policy("/administration", {
      allowDetach: false,
      supportedDevices: ["desktop", "tablet"],
      supportedScreens: ["main", "administration", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: [
      "account_read",
      "grant_read",
      "grant_manage",
      "organization_read",
      "person_read",
      "roster_read",
      "personnel_assignment_manage",
      "asset_read",
      "qualification_read",
      "stan_catalogue_read",
      "map_reference_propose",
      "map_reference_approve",
      "configuration_propose",
      "configuration_review",
      "configuration_activate",
      "configuration_retire",
      "form_template_read",
      "form_template_propose",
      "form_template_review",
      "form_template_approve",
      "form_template_activate",
      "form_template_retire",
      "audit_read",
      "icon_catalog_read",
      "icon_catalog_publish",
      "icon_catalog_audit",
      "retention_policy_manage",
      "legal_hold_manage",
      "manual_disposition_execute",
    ],
    surfaces: ["panel", "route"],
    title: { de: "Administration", en: "Administration" },
  },
  {
    availability: "ready",
    description: {
      de: "Verpflegungsanforderungen, Auslieferung und Empfangsbestätigung",
      en: "Catering requests, delivery, and receipt confirmation",
    },
    id: "logistics",
    /** Redirects into the incident-bound logistics panel. */
    incidentContext: "required",
    operationsSection: "logistics",
    load: () => import("../../features/logistics/logistics-route"),
    moduleCode: "M16",
    policy: policy("/modules/logistics"),
    permissionMode: "any",
    requiredPermissions: [
      "logistics_request_create",
      "logistics_request_process",
      "logistics_delivery_manage",
      "logistics_receipt_confirm",
    ],
    surfaces: ["panel", "route"],
    title: { de: "Logistik", en: "Logistics" },
  },
  {
    availability: "ready",
    description: {
      de: "Einsatzübersicht und Einsatztagebuch in einem konfigurierbaren Arbeitsbereich",
      en: "Mission overview and operational log in a configurable workspace",
    },
    id: "mission-console",
    /** Registers missions, which may precede an incident link. */
    incidentContext: "optional",
    operationsSection: "mission-board",
    load: () => import("../routes/mission-route"),
    moduleCode: "M08",
    policy: policy("/mission", {
      allowDetach: false,
      loadingPriority: "normal",
      supportedScreens: ["main", "situation", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: [
      "mission_read",
      "mission_create",
      "mission_activate",
      "mission_close",
      "etb_promotion_propose",
      "etb_promotion_review",
      "etb_entry_append",
      "etb_entry_correct",
    ],
    surfaces: ["panel", "route"],
    title: { de: "Einsatzführung", en: "Operations" },
  },
  {
    availability: "ready",
    description: {
      de: "Einsatztagebuch (ETB): revisionssichere Einträge, Freigaben und Korrekturen — rollenbezogen (ETB-Führung)",
      en: "Operational log (ETB): audit-proof entries, approvals and corrections — role-specific (ETB keeper)",
    },
    id: "operational-log",
    /** The ETB is a record of one incident. */
    incidentContext: "required",
    load: () => import("../routes/operational-log-route"),
    moduleCode: "M15",
    // Incident-bound: hosted by the shared module workspace, whose left border
    // carries the one Active-incidents selector this screen may use. The old
    // `/modules/operational-log` bookmark still resolves — `FeatureRoute`
    // redirects every `/?open=` descriptor — so no link ever dead-ends.
    policy: policy("/?open=operational-log", {
      allowDetach: false,
      loadingPriority: "normal",
      supportedScreens: ["main", "situation", "tablet-command"],
    }),
    // Any operational viewer (`mission_read`) can OPEN the ETB to read the
    // shared operational record; the ETB-keeper permissions below gate the
    // append/correct/handover ACTIONS inside `EtbLogView`, not discovery. This
    // is the role model — everyone in the operation reads the ETB, only the
    // Einsatztagebuchführung writes it.
    permissionMode: "any",
    requiredPermissions: [
      "mission_read",
      "etb_entry_append",
      "etb_entry_correct",
      "etb_promotion_propose",
      "etb_promotion_review",
    ],
    surfaces: ["panel", "route"],
    title: { de: "Einsatztagebuch", en: "Operational log" },
  },
  {
    availability: "ready",
    description: {
      de: "Fahrzeuge, Ausstattung und Betriebsmittel",
      en: "Vehicles, equipment and assets",
    },
    id: "assets",
    /** Retired stub pointing at Administration. */
    incidentContext: "none",
    administrationSection: "assets",
    load: () => import("../../features/assets/assets-route"),
    moduleCode: "M06",
    policy: policy("/administration?section=assets", {
      supportedDevices: ["desktop", "tablet"],
      supportedScreens: ["main", "administration", "tablet-command"],
    }),
    requiredPermissions: ["asset_read"],
    surfaces: ["panel", "route"],
    title: { de: "Ausstattung", en: "Assets" },
  },
  {
    availability: "ready",
    description: {
      de: "Manuelle Einsatzbereitschaft und Nachweise",
      en: "Manual readiness decisions and evidence",
    },
    id: "readiness",
    /** Organization readiness; an incident only narrows it. */
    incidentContext: "optional",
    operationsSection: "readiness-status",
    load: () => import("../../features/readiness/readiness-route"),
    moduleCode: "M07",
    policy: policy("/modules/readiness", {
      supportedDevices: ["desktop", "tablet"],
      supportedScreens: ["main", "situation", "tablet-command"],
    }),
    requiredPermissions: ["readiness_read"],
    surfaces: ["panel", "route"],
    title: { de: "Einsatzbereitschaft", en: "Readiness" },
  },
  {
    availability: "ready",
    description: {
      de: "Versionierte Formulare und operative Meldungen",
      en: "Versioned forms and operational messages",
    },
    id: "forms",
    /** Governed templates list without one; reports need a link. */
    incidentContext: "optional",
    load: () => import("../../features/forms/forms-route"),
    moduleCode: "M13",
    policy: policy("/modules/forms", { offline: true }),
    permissionMode: "any",
    requiredPermissions: ["form_read", "form_create", "form_submit", "form_route", "form_approve", "form_acknowledge"],
    surfaces: ["panel", "route"],
    title: { de: "Formulare", en: "Forms" },
  },
  {
    availability: "ready",
    description: {
      de: "Governed Kartenreferenzen und Lageobjekte",
      en: "Governed map references and situation objects",
    },
    id: "situation-mapping",
    /** Redirects into the incident-scoped COP. */
    incidentContext: "required",
    operationsSection: "situation-map",
    load: () => import("../../features/situation/situation-route"),
    moduleCode: "M14",
    policy: policy("/modules/situation-mapping", {
      allowMultiple: true,
      featureFlag: "situation-mapping",
      minSize: { height: 360, width: 480 },
    }),
    permissionMode: "any",
    requiredPermissions: ["map_object_read", "map_object_create", "map_object_review", "map_object_publish"],
    surfaces: ["panel", "route"],
    title: { de: "Lagekarte", en: "Situation map" },
  },
  {
    availability: "ready",
    description: {
      de: "Autorisierte operative Kanäle und Nachrichten",
      en: "Authorized operational channels and messages",
    },
    id: "collaboration",
    /** Channels and the event stream are incident-keyed. */
    incidentContext: "required",
    load: () => import("../../features/collaboration/collaboration-route"),
    moduleCode: "M26",
    // Incident-bound: hosted by the shared module workspace, whose left border
    // carries the one Active-incidents selector this screen may use.
    policy: policy("/?open=collaboration"),
    // Mirrors PROTECTED_VIEW_ACCESS_RULES: channel_read is the only permission
    // any M26 endpoint checks, so it is the whole gate. Adding mission_read
    // here would hide chat from operators the API would serve.
    permissionMode: "all",
    requiredPermissions: ["channel_read"],
    surfaces: ["panel", "route"],
    title: { de: "Zusammenarbeit", en: "Collaboration" },
  },
  {
    availability: "ready",
    description: {
      de: "Einsatzstruktur und manuell geregelter F\u00fchrungskontext",
      en: "Incident structure and manually governed command context",
    },
    id: "command-context",
    /** Needs the incident catalogue, not a selected incident. */
    incidentContext: "optional",
    operationsSection: "command-structure",
    load: () => import("../../features/command-context/command-context-route"),
    moduleCode: "M09",
    policy: policy("/modules/command-context", {
      supportedDevices: ["desktop", "tablet"],
      supportedScreens: ["main", "situation", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: ["command_structure_read", "command_structure_manage", "command_change_propose", "command_change_review", "command_action_execute"],
    surfaces: ["panel", "route"],
    title: { de: "Einsatzstruktur", en: "Command context" },
  },
  {
    availability: "ready",
    description: {
      de: "Registrierungen unabhängig prüfen und anwenden",
      en: "Independently approve and apply registrations",
    },
    id: "registration",
    /** Force registration is scoped to the incident's mission. */
    incidentContext: "required",
    load: () => import("../../features/registration/registration-route"),
    moduleCode: "M17",
    // Incident-bound: hosted by the shared module workspace, whose left border
    // carries the one Active-incidents selector this screen may use.
    policy: policy("/?open=registration", { offline: true }),
    permissionMode: "any",
    requiredPermissions: ["registration_approve", "registration_apply"],
    surfaces: ["panel", "route"],
    title: { de: "Registrierung", en: "Registration" },
  },
  {
    availability: "ready",
    description: {
      de: "Freigegebenen Bedarf über StAN-Abgleich, Bereitschaft, Zusage und Zuteilung bearbeiten",
      en: "Work approved demand through StAN matching, readiness, commitment, and allocation",
    },
    id: "capability-resources",
    /** Requirements and commitments belong to one incident. */
    incidentContext: "required",
    load: () => import("../../features/capability-resources/capability-resources-route"),
    moduleCode: "M10",
    // Incident-bound: hosted by the shared module workspace, whose left border
    // carries the one Active-incidents selector this screen may use.
    policy: policy("/?open=capability-resources", {
      allowDetach: false,
      loadingPriority: "normal",
      supportedDevices: ["desktop", "tablet"],
      supportedScreens: ["main", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: [
      "mission_read",
      "requirement_read",
      "request_create",
      "request_review",
      "request_escalate",
      "availability_respond",
      "resource_commit",
      "resource_allocate",
      "resource_release",
      "readiness_read",
      "readiness_decide_manual",
      "stan_catalogue_read",
    ],
    surfaces: ["route"],
    title: { de: "Kräfte & Mittel", en: "Capabilities & Resources" },
  },
  {
    availability: "ready",
    description: {
      de: "Bedarf, Verfügbarkeit, Zusagen und Zuteilungen steuern",
      en: "Manage demand, availability, commitments and allocations",
    },
    id: "resource-requests",
    /** Requests are read from `/incidents/{id}/requirements`. */
    incidentContext: "required",
    operationsSection: "resource-requests",
    load: () => import("../../features/resource-requests/resource-requests-route"),
    moduleCode: "M10",
    policy: policy("/modules/resource-requests"),
    permissionMode: "any",
    requiredPermissions: ["request_create", "request_review", "availability_respond", "resource_commit", "resource_allocate", "resource_release"],
    surfaces: ["panel", "route"],
    title: { de: "Ressourcenanforderungen", en: "Resource requests" },
  },
  {
    availability: "ready",
    description: {
      de: "LuK-Aktivierungen, Besetzung und zugewiesene Arbeit bearbeiten",
      en: "Operate LuK activations, staffing and assigned work",
    },
    id: "luk-work",
    /** LuK scope comes only from the shared selection. */
    incidentContext: "required",
    load: () => import("../../features/luk-work/luk-work-route"),
    moduleCode: "M11",
    // Incident-bound: hosted by the shared module workspace (Active-incidents
    // rail in the left border), never with a selector of its own.
    policy: policy("/?open=luk-work"),
    permissionMode: "any",
    requiredPermissions: ["luk_activate", "luk_staff", "personnel_assign_luk_staff", "luk_work_manage", "luk_deactivate"],
    surfaces: ["panel", "route"],
    title: { de: "LuK-Arbeit", en: "LuK work" },
  },
  {
    availability: "ready",
    description: {
      de: "Versionsgebundene Aufträge, Bestätigungen und Reiseentscheidungen",
      en: "Version-bound orders, acknowledgements and travel decisions",
    },
    id: "orders",
    /** Orders and closure gates belong to one incident. */
    incidentContext: "required",
    load: () => import("../../features/mission-orders-workflow/mission-orders-workflow-route"),
    moduleCode: "M12",
    policy: policy("/?open=orders", {
      allowDetach: false,
      loadingPriority: "normal",
      supportedScreens: ["main", "tablet-command", "mobile-field"],
    }),
    permissionMode: "any",
    requiredPermissions: ["order_read", "order_create", "order_issue", "order_delivery_record", "order_acknowledge", "order_complete", "order_cancel", "order_authorize_deployment", "travel_authorization_decide", "command_structure_read", "command_structure_manage", "mission_close"],
    surfaces: ["route"],
    title: { de: "Einsatzaufträge", en: "Mission Orders" },
  },
  {
    availability: "ready",
    description: {
      de: "StAN-Katalog: Einheitentypen, Aufgaben und Einsatzoptionen — versionsgebunden, mit Belegen",
      en: "StAN catalogue: unit types, tasks and deployment options — versioned, with evidence",
    },
    id: "stan-catalogue",
    /** Governed master data. */
    incidentContext: "none",
    administrationSection: "stan",
    load: () => import("../../features/stan-catalogue/stan-catalogue-route"),
    moduleCode: "M05",
    policy: policy("/administration?section=stan", {
      supportedDevices: ["desktop", "tablet"],
      supportedScreens: ["main", "administration", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: [
      "stan_catalogue_read",
      "stan_catalogue_propose",
      "stan_catalogue_review",
      "stan_catalogue_owner_approve",
      "stan_catalogue_activate",
    ],
    surfaces: ["route"],
    title: { de: "StAN-Katalog", en: "StAN catalogue" },
  },
  {
    availability: "ready",
    description: {
      de: "Personendaten und strukturelle StAN-Positionszuweisungen (PD-033) — keine Kommandobefugnis",
      en: "Person records and structural StAN-position assignments (PD-033) — no command authority",
    },
    id: "personnel",
    /** Scoped to the active organization unit. */
    incidentContext: "none",
    administrationSection: "personnel",
    load: () => import("../../features/personnel/personnel-route"),
    moduleCode: "M03",
    policy: policy("/administration?section=personnel", {
      supportedDevices: ["desktop", "tablet"],
      supportedScreens: ["main", "administration", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: ["person_read", "person_manage_basic", "roster_read", "personnel_assignment_manage"],
    surfaces: ["panel", "route"],
    title: { de: "Personal", en: "Personnel" },
  },
  {
    availability: "ready",
    description: {
      de: "Anforderungen registrieren, Fachberatung prüfen, entscheiden und an die Kräfteplanung übergeben",
      en: "Register requests, review technical advice, decide and hand approved demand to force planning",
    },
    id: "incident-intake",
    /** This is where an incident is created; gating it is a dead end. */
    incidentContext: "none",
    load: () => import("../../features/incident-intake/incident-intake-route"),
    moduleCode: "M08",
    policy: policy("/modules/incident-intake", {
      supportedDevices: ["desktop", "tablet"],
      supportedScreens: ["main", "situation", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: [
      "assistance_request_read",
      "assistance_request_register",
      "assistance_request_review",
      "assistance_request_decide",
      "assessment_review",
      "requirement_propose",
      "requirement_review",
    ],
    surfaces: ["panel", "route"],
    title: { de: "Anforderungen", en: "Assistance requests" },
  },
  {
    availability: "ready",
    description: {
      de: "Fachberaterauftrag bestätigen, Anfahrt melden und Lagefeststellung einreichen",
      en: "Acknowledge adviser tasks, report travel and submit field assessments",
    },
    id: "adviser-field",
    /** Resolves its incident from the adviser's assignment. */
    incidentContext: "optional",
    load: () => import("../../features/adviser-field/adviser-field-route"),
    moduleCode: "M08",
    policy: policy("/modules/adviser-field", {
      supportedDevices: ["desktop", "tablet", "mobile"],
      supportedScreens: ["main", "mobile-field", "tablet-command"],
    }),
    permissionMode: "any",
    requiredPermissions: [
      "adviser_assignment_read",
      "field_assessment_edit",
      "field_assessment_submit",
    ],
    surfaces: ["panel", "route"],
    title: { de: "Fachberater", en: "Technical adviser" },
  },
] as const satisfies readonly FeatureDescriptor[];
